import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { format, startOfMonth } from 'date-fns'
import styled from 'styled-components'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import SettingsNavigation from './SettingsNavigation'
import { useUser } from '@/v2/hooks/useUser'
import { useLogout } from '@/v2/hooks/auth/useAuthentication'
import { useClinic } from '@/v2/hooks/useClinic'
import { useAllowedClinics } from '@/v2/hooks/useAllowedClinics'
import { getHostname } from '@/v2/utils/hostname'
import { headers } from '@/v2/constants/requests'
import { SIDEBAR_WIDTH, DATE_PARAM_FORMAT } from '@/v2/constants'

const SidebarWrapper = styled.div`
  width: ${SIDEBAR_WIDTH};
  height: 100vh;
  background-color: #000000;
  display: flex;
  flex-direction: column;
`

const Topbar = styled.div`
  padding: 16px 1rem;
  border-bottom: 1px solid #1f2937;
  display: flex;
  align-items: center;
  background-color: #000000;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #ffffff;
  cursor: pointer;
  padding: 0;
  border-radius: 0.375rem;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #9ca3af;
  }

  svg {
    width: 30px;
    height: 30px;
  }
`

const NavigationContainer = styled.div`
  flex: 1;
  padding: 0 1rem 1rem 1rem;
  overflow-y: auto;
  background-color: #000000;
`

const ProfileSection = styled.div`
  padding: 0.5rem 1rem;
  border-bottom: 1px solid #1f2937;
  margin-left: -1rem;
  margin-right: -1rem;
  margin-bottom: 0.75rem;
`

const ProfileHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.625rem;
`

const UserAvatar = styled.div`
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background-color: #6366f1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  flex-shrink: 0;
`

const UserInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const UserName = styled.p`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0.02em;
  color: #ffffff;
  margin: 0;
`

const SignOutLink = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0;
  cursor: pointer;
  padding: 0;
  transition: color 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;

  &:hover {
    color: #ef4444;
  }
`

const ClinicSection = styled.div`
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #1f2937;
  margin-left: -1rem;
  margin-right: -1rem;
  padding-left: 1rem;
  padding-right: 1rem;
  margin-bottom: 0.75rem;
`

const ClinicSwitcherButton = styled.button`
  display: flex;
  align-items: center;
  width: 100%;
  background: none;
  border: none;
  padding: 0;
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};
  text-align: left;
  border-radius: 0.375rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${props =>
      props.$clickable ? '#1f2937' : 'transparent'};
  }
`

const ClinicInfo = styled.div`
  flex: 1;
  min-width: 0;
`

const ClinicName = styled.h3`
  font-size: 0.875rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.125rem 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ClinicAddress = styled.p`
  font-size: 0.6875rem;
  color: #9ca3af;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ChevronWrapper = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  flex-shrink: 0;
  margin-left: 0.5rem;
`

const FooterSection = styled.div`
  padding: 0.75rem 1rem;
  border-top: 1px solid #1f2937;
  background-color: #000000;
`

const PoweredBy = styled.p`
  font-size: 0.625rem;
  color: #6b7280;
  margin: 0;
  text-align: left;

  a {
    color: #9ca3af;
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
      color: #ffffff;
    }
  }
`

const buildClinicUrl = subdomain => {
  const { domain, type } = getHostname()
  const port = window.location.port ? `:${window.location.port}` : ''
  return `${window.location.protocol}//${subdomain}.${domain}.${type}${port}${window.location.pathname}`
}

const switchClinic = async subdomain => {
  try {
    const response = await fetch(
      `${window.location.origin}/v2/rest/sessions/switch`,
      {
        method: 'POST',
        headers,
        body: JSON.stringify({ session: { subdomain } }),
      }
    )
    if (!response.ok) {
      throw new Error(`Failed to switch clinic: ${response.statusText}`)
    }
    window.location.assign(buildClinicUrl(subdomain))
  } catch (error) {
    console.error('Error switching clinic:', error)
  }
}

const SettingsSidebar = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('general')
  const [clinicAnchorEl, setClinicAnchorEl] = useState(null)

  const { user } = useUser()
  const { logout } = useLogout()
  const { clinic } = useClinic()
  const { clinics } = useAllowedClinics()

  const { subdomain: currentSubdomain } = getHostname()
  const hasMultipleClinics = clinics.length > 1

  useEffect(() => {
    const pathParts = location.pathname.split('/')
    const settingsIndex = pathParts.indexOf('settings')
    const currentTab =
      settingsIndex >= 0 && pathParts[settingsIndex + 1]
        ? pathParts[settingsIndex + 1]
        : 'general'
    setActiveTab(currentTab)
  }, [location.pathname])

  const handleTabChange = tabId => {
    setActiveTab(tabId)
    navigate(`/v2/settings/${tabId}`)
  }

  const handleClose = () => {
    const rootPath = `/v2/calendar/month/${format(
      startOfMonth(Date.now()),
      DATE_PARAM_FORMAT
    )}`
    navigate(rootPath)
  }

  const handleSignOut = () => {
    logout(null, {
      onSuccess: () => {
        sessionStorage.setItem('isLoggedIn', 'false')
        navigate('/v2/login')
      },
      onError: error => {
        console.error('Sign out error:', error)
      },
    })
  }

  const handleClinicOpen = event => {
    if (hasMultipleClinics) setClinicAnchorEl(event.currentTarget)
  }

  const handleClinicClose = () => setClinicAnchorEl(null)

  const handleClinicSelect = selectedClinic => {
    handleClinicClose()
    if (selectedClinic.subdomain !== currentSubdomain) {
      switchClinic(selectedClinic.subdomain)
    }
  }

  const getUserInitials = u => {
    if (!u) return 'U'
    const firstName = u.firstName || u.first_name || ''
    const lastName = u.lastName || u.last_name || ''
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U'
  }

  const getUserFullName = u => {
    if (!u) return 'Loading...'
    const firstName = u.firstName || u.first_name || ''
    const lastName = u.lastName || u.last_name || ''
    return `${firstName} ${lastName}`.trim() || 'Unknown User'
  }

  const getClinicName = () => {
    const matched = clinics.find(c => c.subdomain === currentSubdomain)
    if (matched) return matched.name
    if (clinic?.name) return clinic.name
    const meta = document.querySelector('meta[name="clinic-name"]')
    return meta ? meta.getAttribute('content') : 'Clearworks'
  }

  const getClinicAddress = () => {
    if (!clinic) return ''
    const parts = [clinic.address1, clinic.city].filter(Boolean)
    return parts.join(', ')
  }

  return (
    <SidebarWrapper>
      <Topbar>
        <CloseButton onClick={handleClose} title="Back to Calendar">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </CloseButton>
      </Topbar>
      <NavigationContainer>
        {/* User Profile Section */}
        <ProfileSection>
          <ProfileHeader>
            <UserAvatar>{getUserInitials(user)}</UserAvatar>
            <UserInfo>
              <UserName>{getUserFullName(user)}</UserName>
              <SignOutLink onClick={handleSignOut}>&rarr; Sign Out</SignOutLink>
            </UserInfo>
          </ProfileHeader>
        </ProfileSection>

        {/* Clinic Switcher Section */}
        <ClinicSection>
          <ClinicSwitcherButton
            onClick={handleClinicOpen}
            $clickable={hasMultipleClinics}
          >
            <ClinicInfo>
              <ClinicName>{getClinicName()}</ClinicName>
              <ClinicAddress>{getClinicAddress()}</ClinicAddress>
            </ClinicInfo>
            {hasMultipleClinics && (
              <ChevronWrapper>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 6L8 10L12 6" />
                </svg>
              </ChevronWrapper>
            )}
          </ClinicSwitcherButton>
          {hasMultipleClinics && (
            <Menu
              anchorEl={clinicAnchorEl}
              open={Boolean(clinicAnchorEl)}
              onClose={handleClinicClose}
              autoFocus={false}
              slotProps={{
                paper: {
                  style: { maxHeight: 48 * 6, minWidth: 200 },
                },
              }}
            >
              {clinics.map(c => (
                <MenuItem key={c.id} onClick={() => handleClinicSelect(c)}>
                  <ListItemText
                    primary={c.name}
                    primaryTypographyProps={{
                      fontWeight: c.subdomain === currentSubdomain ? 700 : 400,
                      fontSize: '14px',
                    }}
                  />
                </MenuItem>
              ))}
            </Menu>
          )}
        </ClinicSection>

        <SettingsNavigation
          activeTab={activeTab}
          onTabChange={handleTabChange}
        />
      </NavigationContainer>
      <FooterSection>
        <PoweredBy>
          Powered By{' '}
          <a
            href="https://clearworks.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Clearworks
          </a>
        </PoweredBy>
      </FooterSection>
    </SidebarWrapper>
  )
}

export default SettingsSidebar
